import {
  fetchApi,
  fetchRest,
  getApiBaseUrl,
  getGraphQLUrl,
} from "@/config/api";

export interface AdminEfpVendeur {
  id_vendeur: number;
  nomBoutique: string;
  nomSociete: string;
  email: string;
}

export interface AdminEfpVendeurDetails {
  id_vendeur: number;
  nomBoutique: string | null;
  nomSociete: string | null;
  email: string | null;
  prenomContact: string | null;
  nomContact: string | null;
  statutEfashion: string;
  statutCompte: string;
  adresse: string | null;
  telephone: string | null;
  dateInscription: string | null;
}

export interface AdminEfpAdmin {
  prenom: string | null;
  nom: string | null;
  email: string;
  isSuperAdmin: boolean;
  permissions: string[];
}

export interface AdminEfpNotification {
  idNotification: number;
  type: string;
  title: string;
  message: string | null;
  entityType: string | null;
  entityId: number | null;
  idCommande: number | null;
  idCommandeGroupe: number | null;
  payload: Record<string, unknown>;
  status: "pending" | "resolved";
  isRead: boolean;
  createdAt: string;
  resolvedAt: string | null;
}

export async function adminEfpGetNotifications(): Promise<{
  count: number;
  items: AdminEfpNotification[];
}> {
  const response = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/notifications?status=pending&limit=100`,
  );
  if (!response.ok) {
    throw new Error("Impossible de charger les notifications");
  }
  return response.json();
}

export async function adminEfpMarkNotificationRead(
  idNotification: number,
): Promise<void> {
  const response = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/notifications/${idNotification}/read`,
    { method: "POST" },
  );
  if (!response.ok) {
    throw new Error("Impossible de marquer la notification comme lue");
  }
}

export async function adminEfpResolveNotification(
  idNotification: number,
): Promise<void> {
  const response = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/notifications/${idNotification}/resolve`,
    { method: "POST" },
  );
  if (!response.ok) throw new Error("Impossible de traiter la notification");
}

export function adminEfpNotificationsStreamUrl(): string {
  return `${getApiBaseUrl()}/api/admin-efp/notifications/stream`;
}

export interface AdminAccessUser {
  id_adminuser: number;
  email: string;
  prenom: string | null;
  nom: string | null;
  isSuperAdmin: boolean;
  permissions: string[];
}

export interface CreateAdminAccessUserInput {
  prenom: string;
  nom: string;
  email: string;
  password: string;
  permissions: string[];
  isSuperAdmin?: boolean;
}

export async function adminEfpLogin(
  email: string,
  password: string
): Promise<{ ok: boolean; admin: AdminEfpAdmin }> {
  const data = await fetchApi<{
    adminEfpLogin: { ok: boolean; admin: AdminEfpAdmin };
  }>(
    `mutation AdminEfpLogin($email: String!, $password: String!) {
      adminEfpLogin(email: $email, password: $password) {
        ok
        admin { prenom nom email isSuperAdmin permissions }
      }
    }`,
    { email: email.trim(), password }
  );
  return data.adminEfpLogin;
}

export async function adminEfpValidate(): Promise<{
  ok: boolean;
  admin: AdminEfpAdmin;
}> {
  const data = await fetchApi<{
    adminEfpValidate: { ok: boolean; admin: AdminEfpAdmin };
  }>(
    `query AdminEfpValidate {
      adminEfpValidate { ok admin { prenom nom email isSuperAdmin permissions } }
    }`,
    undefined,
    { skipAuthRedirect: true },
  );
  return data.adminEfpValidate;
}

function adminAccessUrl(path: string): string {
  return `${getApiBaseUrl()}${path}`;
}

export async function adminEfpListAccessUsers(): Promise<AdminAccessUser[]> {
  const response = await fetchRest(
    adminAccessUrl("/api/admin-efp/access/users")
  );
  if (!response.ok) throw new Error("Impossible de charger les collaborateurs");
  return response.json();
}

export async function adminEfpCreateAccessUser(
  input: CreateAdminAccessUserInput
): Promise<AdminAccessUser> {
  const response = await fetchRest(
    adminAccessUrl("/api/admin-efp/access/users"),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }
  );
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.message || "Impossible de créer l’administrateur");
  }
  return response.json();
}

export async function adminEfpSaveAccessUser(
  idAdminUser: number,
  permissions: string[]
): Promise<AdminAccessUser> {
  const response = await fetchRest(
    adminAccessUrl(`/api/admin-efp/access/users/${idAdminUser}`),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ permissions }),
    }
  );
  if (!response.ok) throw new Error("Impossible d’enregistrer les permissions");
  return response.json();
}

export async function adminEfpDeleteAccessUser(
  idAdminUser: number
): Promise<void> {
  const response = await fetchRest(
    adminAccessUrl(`/api/admin-efp/access/users/${idAdminUser}`),
    { method: "DELETE" }
  );
  if (!response.ok) throw new Error("Impossible de retirer les permissions");
}

export async function adminEfpSetSuperAdmin(
  idAdminUser: number,
  isSuperAdmin: boolean
): Promise<AdminAccessUser> {
  const response = await fetchRest(
    adminAccessUrl(`/api/admin-efp/access/users/${idAdminUser}/super-admin`),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ isSuperAdmin }),
    }
  );
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(
      payload?.message || "Impossible de mettre à jour le statut super admin"
    );
  }
  return response.json();
}

export async function adminEfpChangeAccessUserPassword(
  idAdminUser: number,
  password: string
): Promise<void> {
  const response = await fetchRest(
    adminAccessUrl(`/api/admin-efp/access/users/${idAdminUser}/password`),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    }
  );
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(
      payload?.message || "Impossible de modifier le mot de passe"
    );
  }
}

export async function adminEfpLogout(): Promise<{ ok: boolean }> {
  const data = await fetchApi<{ adminEfpLogout: { ok: boolean } }>(
    `mutation AdminEfpLogout {
      adminEfpLogout { ok }
    }`
  );
  return data.adminEfpLogout;
}

export async function adminEfpAutocompleteVendeurs(
  term: string
): Promise<AdminEfpVendeur[]> {
  const data = await fetchApi<{
    adminEfpVendeursAutocomplete: AdminEfpVendeur[];
  }>(
    `query AdminEfpVendeursAutocomplete($term: String!) {
      adminEfpVendeursAutocomplete(term: $term) {
        id_vendeur nomBoutique nomSociete email
      }
    }`,
    { term }
  );
  return data.adminEfpVendeursAutocomplete;
}

export interface AdminEfpAcheteur {
  id_acheteur: number;
  nomSociete: string;
  email: string;
  nomContact: string;
  prenomContact: string;
}

export interface AdminEfpAcheteurAdresse {
  type: number | null;
  typeLibelle: string | null;
  societe: string | null;
  contact: string | null;
  ligneAdresse: string | null;
  codePostal: string | null;
  ville: string | null;
  pays: string | null;
  telephone: string | null;
  mobile: string | null;
}

export interface AdminEfpAcheteurBloqueVendeur {
  id_vendeur: number;
  vendeurLabel: string;
  dateDisabled: string | null;
}

export interface AdminEfpAcheteurDetails {
  id_acheteur: number;
  nomSociete: string | null;
  nomContact: string | null;
  prenomContact: string | null;
  email: string | null;
  statut: number;
  statutEfashion: string;
  statutCompte: string;
  tva_intra: string | null;
  eori: string | null;
  site_web: string | null;
  id_pays: string | null;
  pays: string | null;
  LangueDefaut: string;
  dateCreation: string;
  in_tmp: boolean;
  commentaires: string | null;
  active_tva: number;
  regimeTva: string;
  telephone: string | null;
  mobile: string | null;
  adresseLivraison: AdminEfpAcheteurAdresse | null;
  adresseFacturation: AdminEfpAcheteurAdresse | null;
  nbCommandes: number;
  derniereCommande: string | null;
  caTotal: number;
  vendeursBloqueurs: AdminEfpAcheteurBloqueVendeur[];
}

export async function adminEfpAutocompleteAcheteurs(
  term: string
): Promise<AdminEfpAcheteur[]> {
  const data = await fetchApi<{
    adminEfpAcheteursAutocomplete: AdminEfpAcheteur[];
  }>(
    `query AdminEfpAcheteursAutocomplete($term: String!) {
      adminEfpAcheteursAutocomplete(term: $term) {
        id_acheteur nomSociete email nomContact prenomContact
      }
    }`,
    { term }
  );
  return data.adminEfpAcheteursAutocomplete;
}

export async function adminEfpGetAcheteurDetails(
  id: number
): Promise<AdminEfpAcheteurDetails> {
  const data = await fetchApi<{
    adminEfpAcheteurDetails: AdminEfpAcheteurDetails;
  }>(
    `query AdminEfpAcheteurDetails($id: Int!) {
      adminEfpAcheteurDetails(id: $id) {
        id_acheteur nomSociete nomContact prenomContact email
        statut statutEfashion statutCompte tva_intra eori site_web id_pays pays LangueDefaut
        dateCreation in_tmp commentaires active_tva regimeTva
        telephone mobile
        adresseLivraison {
          type typeLibelle societe contact ligneAdresse
          codePostal ville pays telephone mobile
        }
        adresseFacturation {
          type typeLibelle societe contact ligneAdresse
          codePostal ville pays telephone mobile
        }
        nbCommandes derniereCommande caTotal
        vendeursBloqueurs { id_vendeur vendeurLabel dateDisabled }
      }
    }`,
    { id }
  );
  return data.adminEfpAcheteurDetails;
}

export async function adminEfpGetCustomerAuthUrl(id: number): Promise<string> {
  const data = await fetchApi<{ adminEfpCustomerAuthUrl: string }>(
    `query AdminEfpCustomerAuthUrl($id: Int!) {
      adminEfpCustomerAuthUrl(id: $id)
    }`,
    { id }
  );
  return data.adminEfpCustomerAuthUrl;
}

export async function adminEfpGetShippingInvoiceUrl(
  orderId: number,
): Promise<string> {
  const response = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/commandes/${orderId}/facture-frais-port`,
    { method: "GET" },
  );
  if (!response.ok) {
    const body = await response
      .json()
      .catch(() => ({ message: "Impossible de récupérer la facture" }));
    throw new Error(body.message || "Impossible de récupérer la facture");
  }
  const data = (await response.json()) as { url?: string };
  if (!data.url) {
    throw new Error("Facture de frais de port introuvable");
  }
  return data.url.startsWith("http")
    ? data.url
    : `${getApiBaseUrl()}${data.url}`;
}

export async function adminEfpGetCreditNoteAuthUrl(
  id: number | string,
  creditNoteId: number | string
): Promise<string> {
  const idAcheteur = Number(id);
  const idCommandeGroupe = Number(creditNoteId);
  if (!Number.isInteger(idAcheteur) || !Number.isInteger(idCommandeGroupe)) {
    throw new Error("Identifiants acheteur ou commande groupe invalides");
  }
  const data = await fetchApi<{ adminEfpCustomerAuthUrl: string }>(
    `query AdminEfpCreditNoteAuthUrl($id: Int!, $creditNoteId: Int!) {
      adminEfpCustomerAuthUrl(id: $id, creditNoteId: $creditNoteId)
    }`,
    { id: idAcheteur, creditNoteId: idCommandeGroupe }
  );
  return data.adminEfpCustomerAuthUrl;
}

export async function adminEfpGetVendeurDetails(
  id: number
): Promise<AdminEfpVendeurDetails> {
  const data = await fetchApi<{
    adminEfpVendeurDetails: AdminEfpVendeurDetails;
  }>(
    `query AdminEfpVendeurDetails($id: Int!) {
      adminEfpVendeurDetails(id: $id) {
        id_vendeur nomBoutique nomSociete email prenomContact nomContact
        statutEfashion statutCompte adresse telephone dateInscription
      }
    }`,
    { id }
  );
  return data.adminEfpVendeurDetails;
}

export interface AdminEfpImpersonationStatus {
  active: boolean;
  adminDisplayName: string | null;
}

export type AdminEfpNewAccountType = "acheteurs" | "vendeurs";

interface AdminEfpNewAccountBase {
  dateCreation: string;
  nomSociete: string | null;
  nomContact: string | null;
  prenomContact: string | null;
  email: string | null;
  statut: number | null;
  codePostal: string | null;
  ville: string | null;
  pays: string | null;
  pays_code: string | null;
}

export interface AdminEfpNewAcheteur extends AdminEfpNewAccountBase {
  id_acheteur: number;
  tva_intra: string | null;
  active_tva: number;
  cartProductCount: number;
  commentaires: string | null;
  corbeille: number;
}

export interface AdminEfpNewVendeur extends AdminEfpNewAccountBase {
  id_vendeur: number;
  nomBoutique: string | null;
  siret: string | null;
  tva: string | null;
  statut_vendeur: number;
  supprimer: number;
  statut_efashion: string | null;
}

export interface AdminEfpVendeurStatusOption {
  id: number;
  libelle: string;
}

export interface AdminEfpNewAccountsResponse {
  type: AdminEfpNewAccountType;
  month: string;
  items: Array<AdminEfpNewAcheteur | AdminEfpNewVendeur>;
  counts: { acheteurs: number; vendeurs: number };
  vendeurStatuses: AdminEfpVendeurStatusOption[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export async function adminEfpGetNewAccounts(params: {
  type: AdminEfpNewAccountType;
  month: string;
  page?: number;
  limit?: number;
  keyword?: string;
  statut?: string;
  activeTva?: string;
  corbeille?: string;
  pays?: string;
}): Promise<AdminEfpNewAccountsResponse> {
  const query = new URLSearchParams({
    type: params.type,
    page: String(params.page ?? 1),
    limit: String(params.limit ?? 50),
  });
  if (params.month) query.set("month", params.month);
  if (params.keyword) query.set("keyword", params.keyword);
  if (params.statut) query.set("statut", params.statut);
  if (params.activeTva) query.set("activeTva", params.activeTva);
  if (params.corbeille) query.set("corbeille", params.corbeille);
  if (params.pays) query.set("pays", params.pays);
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/comptes/nouveaux?${query.toString()}`,
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export type AdminEfpSellerListState = "online" | "offline" | "trash";

export interface AdminEfpManagedSeller {
  id_vendeur: number;
  dateCreation: string | null;
  nomBoutique: string | null;
  nomSociete: string | null;
  nomContact: string | null;
  prenomContact: string | null;
  email: string | null;
  statut: number;
  statut_vendeur: number;
  supprimer: number;
  exclusif: number;
  regulier: number;
  mel: string | number;
  quota_shooting: number;
  quota_venteflash: number;
  quota_shooting_utilise: number;
  quota_campagne_utilise: number;
  commentaires: string | null;
  adresse: string | null;
  codePostal: string | null;
  ville: string | null;
  telephone: string | null;
  zone: string;
  conge_id: number | null;
  debut_conge: string | null;
  fin_conge: string | null;
  dernier_mel: string | null;
  derniere_commande: string | null;
  total_commandes: number;
  date_shooting: string | null;
  delai_confirmation_heures: number | null;
  delai_expedition_heures: number | null;
}

export interface AdminEfpManagedSellersResponse {
  items: AdminEfpManagedSeller[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  counts: { online: number; offline: number; trash: number };
}

export async function adminEfpGetManagedSellers(params: {
  etat: AdminEfpSellerListState;
  keyword?: string;
  zone?: string;
  exclusif?: string;
  regulier?: string;
  mel?: string;
  page?: number;
  limit?: number;
  sort?: string;
  direction?: "asc" | "desc";
}): Promise<AdminEfpManagedSellersResponse> {
  const query = new URLSearchParams({
    etat: params.etat,
    page: String(params.page ?? 1),
    limit: String(params.limit ?? 25),
    sort: params.sort ?? "boutique",
    direction: params.direction ?? "asc",
  });
  for (const [key, value] of Object.entries(params)) {
    if (
      ["etat", "page", "limit", "sort", "direction"].includes(key) ||
      value == null ||
      value === ""
    ) continue;
    query.set(key, String(value));
  }
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/vendeurs/gestion?${query.toString()}`,
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export function adminEfpUpdateManagedSellerConfig(
  id: number,
  field: "exclusif" | "regulier" | "mel" | "quotaShooting" | "quotaVenteflash",
  value: number,
) {
  return postAdminAccountAction<{ ok: boolean; value: number }>(
    `/api/admin-efp/vendeurs/gestion/${id}/config`,
    { field, value },
  );
}

export function adminEfpDisableSellerHoliday(id: number, congeId: number) {
  return postAdminAccountAction<{ ok: boolean }>(
    `/api/admin-efp/vendeurs/gestion/${id}/conge/desactiver`,
    { congeId },
  );
}

export function adminEfpRestoreManagedSeller(id: number) {
  return postAdminAccountAction<{ ok: boolean }>(
    `/api/admin-efp/vendeurs/gestion/${id}/reactiver`,
  );
}

// ---------------------------------------------------------------------------
// Fiche vendeur partagée (création + consultation/modification)
// ---------------------------------------------------------------------------

export interface AdminEfpVendeurFicheAddress {
  id_adresse: number | null;
  numero: string;
  rue: string;
  adresse: string;
  complement: string;
  codePostal: string;
  ville: string;
  id_pays: number | null;
  telephone: string;
  mobile: string;
  fax: string;
}

export interface AdminEfpVendeurFicheMarque {
  id_vendeur_marque?: number | null;
  nom_marque: string;
  defaut: number;
}

export interface AdminEfpVendeurFicheLangue {
  id_langue: string;
  titre: string;
  description: string;
  baseline: string;
}

export interface AdminEfpVendeurFicheSource {
  id_vendeur_source: number;
  libelle: string;
}

export interface AdminEfpVendeurFiche {
  id_vendeur: number;
  sources?: AdminEfpVendeurFicheSource[];
  info: {
    dateCreation: string | null;
    prenomContact: string;
    nomContact: string;
    nomSociete: string;
    nomBoutique: string;
    email: string;
    statut: number;
    statut_vendeur: number;
  };
  adresseMagasin: AdminEfpVendeurFicheAddress;
  adresseFacturation: AdminEfpVendeurFicheAddress;
  similarAdresse: boolean;
  commercial: {
    minimumCommande: number | null;
    tva: string;
    iban: string;
    ibanDefini?: boolean;
    bic: string;
    accountTitle?: string;
    codeSdi: string;
  };
  priceRange: { min: number | null; max: number | null };
  dateCreationBoutique: string;
  marques: AdminEfpVendeurFicheMarque[];
  langues: AdminEfpVendeurFicheLangue[];
  typeProduits: string[];
  commentaires: string;
  siteWeb: string;
  typeDemande: string;
  source: number | null;
  urls: string[];
}

export interface AdminEfpVendeurFichePiece {
  filename: string;
  url: string;
}

/** Charge la fiche vendeur (id = 0 => création, fiche vierge). */
export async function adminEfpGetVendeurFiche(
  id: number,
): Promise<AdminEfpVendeurFiche> {
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/vendeurs/fiche/${id}`,
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/** Enregistre la fiche vendeur (création si id absent/0, sinon mise à jour). */
export function adminEfpSaveVendeurFiche(
  id: number,
  body: Partial<AdminEfpVendeurFiche>,
) {
  const path =
    id && id > 0
      ? `/api/admin-efp/vendeurs/fiche/${id}`
      : `/api/admin-efp/vendeurs/fiche`;
  return postAdminAccountAction<{ ok: boolean; id_vendeur: number }>(
    path,
    body as Record<string, unknown>,
  );
}

/** Liste des pièces jointes (logo / kbis / carte_identite) du vendeur. */
export async function adminEfpGetVendeurFichePieces(
  id: number,
): Promise<{ pieces: AdminEfpVendeurFichePiece[] }> {
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/vendeurs/fiche/${id}/pieces`,
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/** Supprime une pièce jointe du vendeur (S3). */
export function adminEfpRemoveVendeurFichePiece(id: number, filename: string) {
  return postAdminAccountAction<{ ok: boolean }>(
    `/api/admin-efp/vendeurs/fiche/${id}/removefile`,
    { filename },
  );
}

/**
 * Upload des documents du vendeur (logo / kbis / carte d'identité) vers
 * l'endpoint existant /vendors/:id/register-info (stockage S3 Boutiques/{id}).
 */
export async function adminEfpUploadVendeurFichePieces(
  id: number,
  files: { logo?: File | null; kbis?: File | null; carteIdentite?: File | null },
): Promise<{ success: boolean }> {
  const fd = new FormData();
  if (files.logo) fd.append("logo", files.logo);
  if (files.kbis) fd.append("kbis", files.kbis);
  if (files.carteIdentite) fd.append("idGerant", files.carteIdentite);
  const res = await fetchRest(`${getApiBaseUrl()}/vendors/${id}/register-info`, {
    method: "POST",
    body: fd,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function postAdminAccountAction<T>(
  path: string,
  body?: Record<string, unknown>,
): Promise<T> {
  const res = await fetchRest(`${getApiBaseUrl()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export function adminEfpSetAcheteurActive(id: number, active: boolean) {
  return postAdminAccountAction<{ ok: boolean; statut: number }>(
    `/api/admin-efp/comptes/acheteurs/${id}/statut`,
    { active },
  );
}

export function adminEfpToggleAcheteurActiveTva(id: number) {
  return postAdminAccountAction<{
    ok: boolean;
    id_acheteur: number;
  }>(`/api/admin-efp/comptes/acheteurs/${id}/active-tva`);
}

export function adminEfpDeleteAcheteur(id: number) {
  return postAdminAccountAction<{ ok: boolean }>(
    `/api/admin-efp/comptes/acheteurs/${id}/supprimer`,
  );
}

export function adminEfpRestoreAcheteur(id: number) {
  return postAdminAccountAction<{
    ok: boolean;
    statut: number;
    corbeille: number;
  }>(`/api/admin-efp/comptes/acheteurs/${id}/reactiver`);
}

export function adminEfpUpdateAcheteurAction(
  id: number,
  data: { commentaires: string; activeTva: boolean; activites?: number[] },
) {
  return postAdminAccountAction<{
    ok: boolean;
    commentaires: string;
    active_tva: number;
    activites: number[];
  }>(`/api/admin-efp/comptes/acheteurs/${id}/action`, data);
}

export interface AdminEfpActivite {
  id_activite: number;
  libelle_fr: string;
  libelle_uk: string;
  libelle_es: string;
  libelle_it: string;
}

/** Activités acheteurs disponibles + activités sélectionnées (ancien BOA). */
export async function adminEfpGetAcheteurActivites(id: number): Promise<{
  ok: boolean;
  id_acheteur: number;
  activites: AdminEfpActivite[];
  selected: number[];
}> {
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/comptes/acheteurs/${id}/activites`,
    { method: "GET" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export interface AdminEfpAcheteurEmailTemplate {
  key: string;
  label: string;
}

/** Templates email manuels repris de l'ancien BOA (acheteurs-liste popupaddcomment). */
export const ADMIN_EFP_ACHETEUR_EMAIL_TEMPLATES: AdminEfpAcheteurEmailTemplate[] = [
  { key: "acheteur-inscription-hcee", label: "HCEE" },
  { key: "acheteur-inscription-activite-grossiste", label: "Activité grossiste" },
  { key: "acheteur-inscription-acheteur-paris", label: "Activité situé sur paris" },
  { key: "acheteur-notification-inscription", label: "Notification d'inscription" },
  {
    key: "new-acheteur-inscription-international-proofactivity",
    label: "INTER - proof activity",
  },
  { key: "new-acheteur-inscription-kbis-siret", label: "FR - KBIS et SIRET manquants" },
  { key: "new-inscription-activité-incorrecte", label: "FR - Activité incorrecte" },
  { key: "new-kbismanquant", label: "FR - KBIS manquant" },
  { key: "new-acheteur-novat-noproof", label: "EU - VAT et proof missing" },
  {
    key: "new-acheteur-inscription-eu-vatinvalid-proofok",
    label: "EU - VAT invalid - Proof ok",
  },
  { key: "new-acheteur-inscription-uk-eori", label: "UK - EORI missing" },
];

export function adminEfpSendAcheteurEmail(
  id: number,
  data: { template: string; email?: string; langue?: string },
) {
  return postAdminAccountAction<{
    ok: boolean;
    template: string;
    recipient: string;
    redirected: boolean;
  }>(`/api/admin-efp/comptes/acheteurs/${id}/send-email`, data);
}

export function adminEfpSetVendeurActive(id: number, active: boolean) {
  return postAdminAccountAction<{ ok: boolean; statut_vendeur: number }>(
    `/api/admin-efp/comptes/vendeurs/${id}/statut`,
    { active },
  );
}

export function adminEfpSetVendeurEfashionStatus(id: number, statut: number) {
  return postAdminAccountAction<{
    ok: boolean;
    statut: number;
    statut_efashion: string;
  }>(`/api/admin-efp/comptes/vendeurs/${id}/statut-efashion`, { statut });
}

export function adminEfpValidateVendeur(id: number) {
  return postAdminAccountAction<{
    ok: boolean;
    statut: number;
    statut_vendeur: number;
    mel: string;
    mea_month_delay: number;
    statut_efashion: string;
    email_queued: boolean;
    email_recipient: string | null;
    email_error: string | null;
  }>(`/api/admin-efp/comptes/vendeurs/${id}/valider`);
}

export function adminEfpDeleteVendeur(id: number) {
  return postAdminAccountAction<{ ok: boolean }>(
    `/api/admin-efp/comptes/vendeurs/${id}/supprimer`,
  );
}

/** Statut d'impersonation (cookie HttpOnly) — appelle avec credentials pour envoyer le cookie. */
export async function adminEfpImpersonationStatus(): Promise<AdminEfpImpersonationStatus> {
  const res = await fetchRest(getGraphQLUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `query { adminEfpImpersonationStatus { active adminDisplayName } }`,
    }),
    skipAuthRedirect: true,
  });
  if (!res.ok) return { active: false, adminDisplayName: null };
  const json = (await res.json()) as {
    data?: { adminEfpImpersonationStatus?: AdminEfpImpersonationStatus };
  };
  return (
    json.data?.adminEfpImpersonationStatus ?? {
      active: false,
      adminDisplayName: null,
    }
  );
}

export async function adminEfpImpersonate(
  sellerId: number
): Promise<{ ok: boolean; adminDisplayName: string | null }> {
  const data = await fetchApi<{
    adminEfpImpersonate: { ok: boolean; adminDisplayName: string | null };
  }>(
    `mutation AdminEfpImpersonate($sellerId: Int!) {
      adminEfpImpersonate(sellerId: $sellerId) { ok adminDisplayName }
    }`,
    { sellerId }
  );
  return data.adminEfpImpersonate;
}

/** Arrêter l'impersonation (efface le cookie auth-token) — credentials pour envoyer le cookie. */
export async function adminEfpClearImpersonation(): Promise<{ ok: boolean }> {
  const res = await fetchRest(getGraphQLUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `mutation { adminEfpClearImpersonation { ok } }`,
    }),
    skipAuthRedirect: true,
  });
  if (!res.ok) return { ok: false };
  const json = (await res.json()) as {
    data?: { adminEfpClearImpersonation?: { ok: boolean } };
  };
  return json.data?.adminEfpClearImpersonation ?? { ok: false };
}

export interface MelRefreshStats {
  collections: number;
  allCaracs: number;
  caracsByCategory: number;
  compositions: number;
  couleurs: number;
  categoriesFlat: number;
}

/** Met à jour la date d'expédition / groupage d'un commande_groupe. */
export async function adminEfpSetDateExpedition(
  idCommandeGroupe: number,
  dateExpedition: string
): Promise<{ success: boolean; message?: string }> {
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/groupages/set-date-expedition`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idCommandeGroupe, dateExpedition }),
    }
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export interface AdminEfpUpdateFraisPortResult {
  success: boolean;
  message?: string;
  fraisPort?: number;
  fraisPortSrc?: number;
  remboursementPort?: number;
}

/** Modifie les frais de port retenus dans le calcul de rupture/remboursement. */
export async function adminEfpUpdateFraisPort(
  idCommandeGroupe: number,
  newFraisPort: number,
): Promise<AdminEfpUpdateFraisPortResult> {
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/groupages/update-frais-port`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idCommandeGroupe, newFraisPort }),
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function adminEfpRecalculateFraisPort(
  idCommandeGroupe: number,
): Promise<{ success: boolean; message?: string; newFraisPort?: number }> {
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/groupages/recalculate-frais-port`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idCommandeGroupe }),
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/** Régénère le cache des données MEL côté serveur (auth admin cookie). */
export async function adminEfpRefreshMelTemplateData(): Promise<{
  ok: boolean;
  updatedAt: string;
  stats: MelRefreshStats;
}> {
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/mel-template-data/refresh`,
    { method: "POST" }
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/** Soft delete d'une étiquette de commande (admin EFP uniquement). */
export async function adminEfpSoftDeleteEtiquette(
  idCommande: number,
  idEtiquette: number
): Promise<{ ok: boolean; error?: string; id_commande_etiquette?: number }> {
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/commandes/${idCommande}/etiquettes/${idEtiquette}/supprimer`,
    { method: "POST" }
  );
  return res.json();
}

export async function adminEfpSoftDeleteAllEtiquettes(
  idCommande: number,
): Promise<{ ok: boolean; error?: string; deleted?: number }> {
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/commandes/${idCommande}/etiquettes/supprimer-toutes`,
    { method: "POST" },
  );
  return res.json();
}

export interface ShopifyPrivacyRequest {
  id: number;
  topic: string;
  shopDomain: string;
  shopifyRequestId: string | null;
  customerId: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  orders: unknown[];
  note: string | null;
  status: string;
  handledAt: string | null;
  createdAt: string | null;
}

export async function adminEfpShopifyPrivacyRequests(status?: string) {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/shopify/privacy-requests${query}`,
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<ShopifyPrivacyRequest[]>;
}

export async function adminEfpHandleShopifyPrivacyRequest(id: number) {
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/shopify/privacy-requests/${id}/handle`,
    { method: "POST" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ success: boolean }>;
}
